import React, { useState, useRef } from 'react';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Box,
  Input,
  Button,
  ButtonGroup,
  Tooltip,
  IconButton,
  Flex,
  useDisclosure,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Checkbox,
  VStack,
  Text,
  Badge,
  Wrap,
  WrapItem,
  Tag,
  Spacer,
  HStack,
  useColorModeValue,
  Spinner,
  Alert,
  AlertIcon,
  InputGroup,
  InputLeftElement,
  Card,
  CardBody,
  Divider,
  Select,
  useBreakpointValue
} from '@chakra-ui/react';
import { 
  FiEdit2, 
  FiTrash2, 
  FiChevronLeft, 
  FiChevronRight, 
  FiSearch, 
  FiFilter, 
  FiPlus,
  FiMoreVertical,
  FiX,
  FiRefreshCw
} from 'react-icons/fi';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';

const DataTable = ({ 
  columns, 
  data, 
  onSearch, 
  onEdit, 
  onCreate, 
  onDelete,
  searchPlaceholder = "Search...",
  isLoading = false,
  title = "Data Management",
  createButtonText = "Create New",
  emptyStateTitle = "No data found",
  emptyStateDescription = "No data to display",
  loadingText = "Loading...",
  // Filter related props (optional)
  filterConfig = null,
  onFilterChange = null,
  activeFilters = {},
  filterDisplayNames = {},
  // Additional action props
  customActions = [],
  onRefresh = null
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [deleteItem, setDeleteItem] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef();

  // Color mode values - ALL hooks must be called at the top level
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const cardBg = useColorModeValue('white', 'gray.700');
  const searchBg = useColorModeValue('white', 'gray.700');
  const tableHeaderBg = useColorModeValue('gray.50', 'gray.700');
  const tableRowHoverBg = useColorModeValue('gray.50', 'gray.600');
  const tableHeaderHoverBg = useColorModeValue('gray.100', 'gray.600'); // Add this line

  // Responsive values
  const isMobile = useBreakpointValue({ base: true, md: false });
  const showFullPagination = useBreakpointValue({ base: false, md: true });

  // Sort data
  const sortedData = React.useMemo(() => {
    if (!sortConfig.key) return data;
    
    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, sortConfig]);

  // Calculate total pages
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);

  // Get data for the current page
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = sortedData.slice(startIndex, endIndex);

  // Handle sorting
  const handleSort = (key) => {
    if (!key) return;
    
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Handlers for pagination
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page
  };

  const generatePaginationButtons = () => {
    let buttons = [];
    const maxButtons = isMobile ? 3 : 7;

    if (totalPages <= maxButtons) {
      for (let i = 1; i <= totalPages; i++) {
        buttons.push(
          <Button 
            key={i} 
            onClick={() => handlePageChange(i)} 
            variant={i === currentPage ? "solid" : "outline"}
            colorScheme={i === currentPage ? "blue" : "gray"}
            size="sm"
          >
            {i}
          </Button>
        );
      }
    } else {
      buttons.push(
        <Button 
          key={1} 
          onClick={() => handlePageChange(1)} 
          variant={currentPage === 1 ? "solid" : "outline"}
          colorScheme={currentPage === 1 ? "blue" : "gray"}
          size="sm"
        >
          1
        </Button>
      );

      if (currentPage > 3) {
        buttons.push(
          <Button key="dots-left" isDisabled size="sm" variant="ghost">
            …
          </Button>
        );
      }

      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        buttons.push(
          <Button 
            key={i} 
            onClick={() => handlePageChange(i)} 
            variant={i === currentPage ? "solid" : "outline"}
            colorScheme={i === currentPage ? "blue" : "gray"}
            size="sm"
          >
            {i}
          </Button>
        );
      }

      if (currentPage < totalPages - 2) {
        buttons.push(
          <Button key="dots-right" isDisabled size="sm" variant="ghost">
            …
          </Button>
        );
      }

      buttons.push(
        <Button 
          key={totalPages} 
          onClick={() => handlePageChange(totalPages)} 
          variant={currentPage === totalPages ? "solid" : "outline"}
          colorScheme={currentPage === totalPages ? "blue" : "gray"}
          size="sm"
        >
          {totalPages}
        </Button>
      );
    }

    return buttons;
  };

  // Delete handlers
  const handleDeleteClick = (item) => {
    setDeleteItem(item);
    onOpen();
  };

  const handleConfirmDelete = () => {
    if (deleteItem && onDelete) {
      onDelete(deleteItem);
    }
    onClose();
    setDeleteItem(null);
  };

  // Filter-related functions
  const toggleFilterValue = (filterName, value) => {
    if (!onFilterChange) return;
    
    const updatedFilters = { ...activeFilters };
    const filterValues = [...(updatedFilters[filterName] || [])];
    const valueIndex = filterValues.indexOf(value);
    
    if (valueIndex === -1) {
      filterValues.push(value);
    } else {
      filterValues.splice(valueIndex, 1);
    }
    
    updatedFilters[filterName] = filterValues;
    onFilterChange(updatedFilters);
  };

  const removeFilterValue = (filterName, value) => {
    if (!onFilterChange) return;
    
    const updatedFilters = { ...activeFilters };
    updatedFilters[filterName] = (updatedFilters[filterName] || []).filter(v => v !== value);
    onFilterChange(updatedFilters);
  };

  const clearFilter = (filterName) => {
    if (!onFilterChange) return;
    
    const updatedFilters = { ...activeFilters };
    updatedFilters[filterName] = [];
    onFilterChange(updatedFilters);
  };

  const clearAllFilters = () => {
    if (!onFilterChange || !filterConfig) return;
    
    const clearedFilters = {};
    Object.keys(filterConfig).forEach(key => {
      clearedFilters[key] = [];
    });
    
    onFilterChange(clearedFilters);
  };

  // Check if any filters are applied
  const hasActiveFilters = () => {
    if (!activeFilters) return false;
    return Object.values(activeFilters).some(filterValues => 
      Array.isArray(filterValues) && filterValues.length > 0
    );
  };

  // Get display name for filter value
  const getFilterValueDisplay = (filterName, value) => {
    if (filterConfig?.[filterName]?.options) {
      const option = filterConfig[filterName].options.find(opt => 
        opt.id === value || opt.value === value
      );
      
      if (option) {
        return option.name || option.label || value;
      }
    }
    return value;
  };

  // Get color scheme for filter tag
  const getFilterColorScheme = (filterName) => {
    if (filterConfig?.[filterName]?.colorScheme) {
      return filterConfig[filterName].colorScheme;
    }
    
    const defaultSchemes = {
      status: 'green',
      category: 'blue',
      type: 'purple',
      tag: 'orange'
    };
    
    for (const key in defaultSchemes) {
      if (filterName.toLowerCase().includes(key)) {
        return defaultSchemes[key];
      }
    }
    
    return 'gray';
  };

  // Get display name for filter category
  const getFilterDisplayName = (filterName) => {
    if (filterDisplayNames?.[filterName]) {
      return filterDisplayNames[filterName];
    }
    
    return filterName
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase());
  };

  // Get sort icon
  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) return null;
    return sortConfig.direction === 'asc' ? <ChevronUpIcon ml={1} /> : <ChevronDownIcon ml={1} />;
  };

  return (
    <Box>
      {/* Enhanced Header Section */}
      <Card bg={cardBg} borderColor={borderColor} variant="outline" mb={6}>
        <CardBody>
          <VStack spacing={4} align="stretch">
            {/* Title and Create Button Row */}
            <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
              <VStack align="start" spacing={1}>
                <Text fontSize="xl" fontWeight="bold">{title}</Text>
                <Text color="gray.500" fontSize="sm">
                  {data.length} {data.length === 1 ? 'item' : 'items'} total
                  {hasActiveFilters() && `, ${currentData.length} filtered`}
                </Text>
              </VStack>
              
              <HStack spacing={2}>
                {onRefresh && (
                  <Tooltip label="Refresh">
                    <IconButton
                      icon={<FiRefreshCw />}
                      onClick={onRefresh}
                      variant="outline"
                      size="sm"
                      aria-label="Refresh"
                    />
                  </Tooltip>
                )}
                <Button 
                  leftIcon={<FiPlus />}
                  colorScheme="blue" 
                  onClick={onCreate}
                  size={isMobile ? "sm" : "md"}
                >
                  {isMobile ? "Create" : createButtonText}
                </Button>
              </HStack>
            </Flex>

            {/* Search and Filter Row */}
            <Flex wrap="wrap" gap={4} align="center">
              <InputGroup maxW={{ base: "100%", md: "350px" }}>
                <InputLeftElement>
                  <FiSearch color="gray" />
                </InputLeftElement>
                <Input
                  placeholder={searchPlaceholder}
                  onChange={(e) => onSearch(e.target.value)}
                  bg={searchBg}
                  borderColor={borderColor}
                  focusBorderColor="blue.400"
                  _placeholder={{ color: 'gray.500' }}
                />
              </InputGroup>
              
              <Spacer />
              
              {/* Enhanced Filter UI */}
              {filterConfig && (
                <HStack spacing={2} wrap="wrap">
                  <HStack>
                    <FiFilter />
                    <Text fontWeight="medium" fontSize="sm">Filters:</Text>
                  </HStack>
                  
                  {Object.keys(filterConfig).map(filterName => (
                    <Menu closeOnSelect={false} key={filterName}>
                      <MenuButton 
                        as={Button} 
                        rightIcon={<ChevronDownIcon />} 
                        size="sm" 
                        variant="outline"
                        borderColor={borderColor}
                        _hover={{ borderColor: 'blue.300' }}
                      >
                        {getFilterDisplayName(filterName)}
                        {activeFilters[filterName]?.length > 0 && (
                          <Badge ml={2} colorScheme="blue" borderRadius="full">
                            {activeFilters[filterName].length}
                          </Badge>
                        )}
                      </MenuButton>
                      <MenuList maxH="300px" overflowY="auto" borderColor={borderColor}>
                        <VStack align="stretch" spacing={0}>
                          {filterConfig[filterName].options.map(option => (
                            <MenuItem key={option.id || option.value} p={0}>
                              <Checkbox
                                isChecked={
                                  activeFilters[filterName]?.includes(option.id) || 
                                  activeFilters[filterName]?.includes(option.value)
                                }
                                onChange={() => toggleFilterValue(
                                  filterName, 
                                  option.id !== undefined ? option.id : option.value
                                )}
                                w="full"
                                px={4}
                                py={2}
                                _hover={{ bg: "gray.100" }}
                              >
                                {option.name || option.label}
                              </Checkbox>
                            </MenuItem>
                          ))}
                        </VStack>
                        <MenuDivider />
                        <MenuItem 
                          onClick={() => clearFilter(filterName)}
                          color="red.500"
                          icon={<FiX />}
                        >
                          Clear Filter
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  ))}
                  
                  {hasActiveFilters() && (
                    <Button 
                      variant="ghost" 
                      onClick={clearAllFilters}
                      color="red.500"
                      size="sm"
                      leftIcon={<FiX />}
                    >
                      Clear All
                    </Button>
                  )}
                </HStack>
              )}
            </Flex>

            {/* Active Filters Display */}
            {filterConfig && hasActiveFilters() && (
              <>
                <Divider />
                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={2} color="gray.600">
                    Active Filters:
                  </Text>
                  <Wrap spacing={2}>
                    {Object.keys(activeFilters).map(filterName => 
                      activeFilters[filterName]?.map((value, index) => (
                        <WrapItem key={`${filterName}-${value}-${index}`}>
                          <Tag 
                            size="md" 
                            borderRadius="full" 
                            variant="subtle" 
                            colorScheme={getFilterColorScheme(filterName)}
                          >
                            <Text fontSize="xs">
                              {getFilterDisplayName(filterName)}: {getFilterValueDisplay(filterName, value)}
                            </Text>
                            <IconButton
                              icon={<FiX />}
                              size="xs"
                              ml={2}
                              borderRadius="full"
                              variant="ghost"
                              colorScheme={getFilterColorScheme(filterName)}
                              onClick={() => removeFilterValue(filterName, value)}
                              aria-label="Remove filter"
                            />
                          </Tag>
                        </WrapItem>
                      ))
                    )}
                  </Wrap>
                </Box>
              </>
            )}
          </VStack>
        </CardBody>
      </Card>

      {/* Enhanced Data Table */}
      <Card bg={cardBg} borderColor={borderColor} variant="outline">
        <CardBody p={0}>
          {isLoading ? (
            <VStack py={12} spacing={4}>
              <Spinner size="lg" thickness="3px" speed="0.65s" color="blue.500" />
              <Text color="gray.500">{loadingText}</Text>
            </VStack>
          ) : currentData.length === 0 ? (
            <VStack py={12} spacing={4}>
              <Text fontSize="2xl">📋</Text>
              <VStack spacing={2}>
                <Text fontSize="lg" fontWeight="medium" color="gray.500">
                  {emptyStateTitle}
                </Text>
                <Text color="gray.500" textAlign="center" maxW="400px">
                  {emptyStateDescription}
                </Text>
              </VStack>
              {!hasActiveFilters() && (
                <Button leftIcon={<FiPlus />} colorScheme="blue" onClick={onCreate}>
                  {createButtonText}
                </Button>
              )}
            </VStack>
          ) : (
            <>
              <Box overflowX="auto">
                <Table size="md">
                  <Thead bg={tableHeaderBg}>
                    <Tr>
                      {columns.map((column) => (
                        <Th 
                          key={column.key}
                          cursor={column.sortable !== false ? "pointer" : "default"}
                          onClick={() => column.sortable !== false && handleSort(column.key)}
                          borderColor={borderColor}
                          _hover={column.sortable !== false ? { bg: tableHeaderHoverBg } : {}}
                        >
                          <HStack spacing={1}>
                            <Text>{column.label}</Text>
                            {getSortIcon(column.key)}
                          </HStack>
                        </Th>
                      ))}
                      <Th borderColor={borderColor} w="100px">Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {currentData.map((item, index) => (
                      <Tr 
                        key={item.id || index}
                        borderColor={borderColor}
                        _hover={{ bg: tableRowHoverBg }}
                        transition="background-color 0.2s"
                      >
                        {columns.map((column) => (
                          <Td key={`${item.id || index}-${column.key}`} borderColor={borderColor}>
                            {column.render ? column.render(item[column.key], item) : item[column.key]}
                          </Td>
                        ))}
                        <Td borderColor={borderColor}>
                          <HStack spacing={1}>
                            {/* Custom Actions */}
                            {customActions.map((action, actionIndex) => (
                              <Tooltip key={actionIndex} label={action.label}>
                                <IconButton
                                  icon={action.icon}
                                  onClick={() => action.onClick(item)}
                                  aria-label={action.label}
                                  size="sm"
                                  variant="ghost"
                                  colorScheme={action.colorScheme || "gray"}
                                />
                              </Tooltip>
                            ))}

                            {/* Default Actions */}
                            <Menu>
                              <MenuButton
                                as={IconButton}
                                icon={<FiMoreVertical />}
                                size="sm"
                                variant="ghost"
                                aria-label="More actions"
                              />
                              <MenuList borderColor={borderColor}>
                                <MenuItem icon={<FiEdit2 />} onClick={() => onEdit(item)}>
                                  Edit
                                </MenuItem>
                                <MenuItem 
                                  icon={<FiTrash2 />} 
                                  onClick={() => handleDeleteClick(item)}
                                  color="red.500"
                                >
                                  Delete
                                </MenuItem>
                              </MenuList>
                            </Menu>
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>

              {/* Enhanced Pagination */}
              {totalPages > 1 && (
                <Box p={4} borderTop="1px solid" borderColor={borderColor}>
                  <Flex 
                    justify="space-between" 
                    align="center" 
                    wrap="wrap" 
                    gap={4}
                  >
                    {/* Items per page selector */}
                    <HStack spacing={2}>
                      <Text fontSize="sm" color="gray.500">Show:</Text>
                      <Select 
                        size="sm" 
                        value={itemsPerPage} 
                        onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                        w="80px"
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                      </Select>
                      <Text fontSize="sm" color="gray.500">
                        {startIndex + 1}-{Math.min(endIndex, data.length)} of {data.length}
                      </Text>
                    </HStack>

                    {/* Pagination Controls */}
                    <ButtonGroup size="sm" spacing="1">
                      <Tooltip label="Previous">
                        <IconButton 
                          icon={<FiChevronLeft />} 
                          onClick={() => handlePageChange(currentPage - 1)} 
                          isDisabled={currentPage === 1} 
                          aria-label="Previous Page"
                          variant="outline"
                        />
                      </Tooltip>

                      {showFullPagination ? (
                        generatePaginationButtons()
                      ) : (
                        <Text fontSize="sm" px={4} py={2}>
                          {currentPage} / {totalPages}
                        </Text>
                      )}

                      <Tooltip label="Next">
                        <IconButton 
                          icon={<FiChevronRight />} 
                          onClick={() => handlePageChange(currentPage + 1)} 
                          isDisabled={currentPage === totalPages} 
                          aria-label="Next Page"
                          variant="outline"
                        />
                      </Tooltip>
                    </ButtonGroup>
                  </Flex>
                </Box>
              )}
            </>
          )}
        </CardBody>
      </Card>

      {/* Enhanced Delete Confirmation Modal */}
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
        isCentered
      >
        <AlertDialogOverlay backdropFilter="blur(4px)">
          <AlertDialogContent borderColor={borderColor}>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Confirm Deletion
            </AlertDialogHeader>

            <AlertDialogBody>
              <Alert status="warning" borderRadius="md" mb={4}>
                <AlertIcon />
                This action cannot be undone.
              </Alert>
              <Text>
                Are you sure you want to delete{' '}
                <Text as="span" fontWeight="bold">
                  {deleteItem?.name || deleteItem?.title || 'this item'}
                </Text>
                ?
              </Text>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose} variant="outline">
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleConfirmDelete} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default DataTable;
